// Shared configuration for the seven Validation Profile categories.
//
// Consumers:
//   - pages/ProjectDetailPage.js   — checkbox grid on the "run new audit" step
//   - pages/ValidationProfilesPage.js — saved-profile presets & editor
//
// Order here is the display order in the UI (row-major). "Custom BEP
// rules" is placed first so it renders as the full-width banner tile,
// with the remaining categories wrapping into a 3-column grid below it.
//
// The tone classes below are LITERAL Tailwind strings so PurgeCSS keeps
// them in the production build. Never construct these class names by
// concatenation at render time.

export const VALIDATION_PROFILE_OPTIONS = [
  {
    key: 'custom_bep_rules',
    label: 'Custom BEP rules',
    description: 'Applies this project’s active approved BEP rules',
    tone: 'indigo',
  },
  {
    key: 'naming_compliance',
    label: 'Naming compliance',
    description: 'File and element naming checks',
    tone: 'sky',
  },
  {
    key: 'ifc_schema',
    label: 'IFC schema / class mapping',
    description: 'Each IfcClass maps to a valid discipline',
    tone: 'violet',
  },
  {
    key: 'fm_parameters',
    label: 'Asset tagging / FM parameters',
    description: 'MEP assets carry mandatory FM data',
    tone: 'amber',
  },
  {
    key: 'geometry_completeness',
    label: 'Geometry completeness',
    description: 'GlobalId, geometry and basic metadata',
    tone: 'emerald',
  },
  {
    key: 'cobie_handover',
    label: 'COBie / handover data',
    description: 'Reviews MEP assets for handover readiness',
    tone: 'rose',
  },
  {
    key: 'om_matching',
    label: 'O&M matching',
    description: 'Cross-references IFC assets with uploaded O&M manuals',
    tone: 'cyan',
  },
];

export const VALIDATION_TONE_CLASSES = {
  sky: {
    active:
      'border-sky-400 bg-sky-50 dark:border-sky-700 dark:bg-sky-950/30',
    inactive:
      'border-sky-200 bg-sky-50/30 hover:bg-sky-50/70 dark:border-sky-900/60 dark:bg-sky-950/10',
    accent: 'accent-sky-600',
  },
  violet: {
    active:
      'border-violet-400 bg-violet-50 dark:border-violet-700 dark:bg-violet-950/30',
    inactive:
      'border-violet-200 bg-violet-50/30 hover:bg-violet-50/70 dark:border-violet-900/60 dark:bg-violet-950/10',
    accent: 'accent-violet-600',
  },
  amber: {
    active:
      'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30',
    inactive:
      'border-amber-200 bg-amber-50/30 hover:bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/10',
    accent: 'accent-amber-600',
  },
  emerald: {
    active:
      'border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30',
    inactive:
      'border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/10',
    accent: 'accent-emerald-600',
  },
  rose: {
    active:
      'border-rose-400 bg-rose-50 dark:border-rose-700 dark:bg-rose-950/30',
    inactive:
      'border-rose-200 bg-rose-50/30 hover:bg-rose-50/70 dark:border-rose-900/60 dark:bg-rose-950/10',
    accent: 'accent-rose-600',
  },
  cyan: {
    active:
      'border-cyan-400 bg-cyan-50 dark:border-cyan-700 dark:bg-cyan-950/30',
    inactive:
      'border-cyan-200 bg-cyan-50/30 hover:bg-cyan-50/70 dark:border-cyan-900/60 dark:bg-cyan-950/10',
    accent: 'accent-cyan-600',
  },
  indigo: {
    active:
      'border-indigo-400 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/30',
    inactive:
      'border-indigo-200 bg-indigo-50/30 hover:bg-indigo-50/70 dark:border-indigo-900/60 dark:bg-indigo-950/10',
    accent: 'accent-indigo-600',
  },
  teal: {
    active:
      'border-teal-400 bg-teal-50 dark:border-teal-700 dark:bg-teal-950/30',
    inactive:
      'border-teal-200 bg-teal-50/30 hover:bg-teal-50/70 dark:border-teal-900/60 dark:bg-teal-950/10',
    accent: 'accent-teal-600',
  },
};

// Baseline profile with every category present. Consumers that need an
// "all-false" seed can use ``defaultValidationProfile(false)``.
export const defaultValidationProfile = (initial = false) =>
  Object.fromEntries(
    VALIDATION_PROFILE_OPTIONS.map((o) => [o.key, !!initial]),
  );
